// Import necessary dependencies and hooks
import React, {useEffect, useState} from 'react'
import axios from 'axios'
import Select from 'react-select'
import {Form, FormikProvider, useFormik} from 'formik'
import * as Yup from 'yup'
import {useDataLayerValue} from '../../../DataLayer'

// API URL from environment variables
const API_URL = process.env.REACT_APP_API_URL

export type ActiveFilterChip = {
  id: string
  label: string
  value: string
}

// Props type definition for the component
type JobMonitoringFilterProps = {
  setFilterOptions: (options: any) => void
  onActiveFiltersChange?: (chips: ActiveFilterChip[]) => void
  resetToken?: number
}

// Custom styles for the Select component
const customStyles = {
  control: (provided: any) => ({
    ...provided,
    lineHeight: '24px',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 999,
  }),
}

// Predefined status options for the Status dropdown
const statusOptions = [{value: 'N', label: 'No Job Vehicle'}]

// Yup validation schema for form fields
const advanceSerachSchema = Yup.object().shape({
  statusCode: Yup.string(),
  fK_VehicleType: Yup.string(),
  fK_BaseLocation: Yup.string().nullable(),
  regNo: Yup.array(),
  cT_FK_OperatingTypeCode: Yup.string().nullable(),
  cT_FK_CustomerSpecificationCode: Yup.string().nullable(),
})

// Interface defining the structure of filter options
export interface FilterOptions {
  statusCode: string | null
  fK_VehicleType: string | null
  fK_BaseLocation: string | null
  regNo: []
  cT_FK_OperatingTypeCode: string | null
  cT_FK_CustomerSpecificationCode: string | null
}

// Initial values for the filter form
export const filterInitials: FilterOptions = {
  statusCode: '',
  fK_VehicleType: '',
  fK_BaseLocation: '',
  regNo: [],
  cT_FK_OperatingTypeCode: '',
  cT_FK_CustomerSpecificationCode: '',
}

function toAppliedFilterOptions(values: FilterOptions) {
  return {
    statusCode: values.statusCode != '' ? values.statusCode : null,
    fK_VehicleType: values.fK_VehicleType != '' ? values.fK_VehicleType : null,
    fK_BaseLocation: values.fK_BaseLocation !== '' ? values.fK_BaseLocation : null,
    regNo: values.regNo,
    fK_VehicleFeature: null,
    fK_VehicleCode: null,
    cT_FK_OperatingTypeCode:
      values.cT_FK_OperatingTypeCode != '' ? values.cT_FK_OperatingTypeCode : null,
    cT_FK_CustomerSpecificationCode:
      values.cT_FK_CustomerSpecificationCode != ''
        ? values.cT_FK_CustomerSpecificationCode
        : null,
  }
}

function buildActiveFilterChips(
  values: FilterOptions,
  lookups: {
    vehicleTypes: any[]
    allBaseLocations: any[]
    allVehicles: any[]
    operatingTypes: any[]
    customerSpecifications: any[]
  }
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = []

  if (values.statusCode) {
    const label =
      statusOptions.find((item) => item.value === values.statusCode)?.label ??
      String(values.statusCode)
    chips.push({id: 'statusCode', label: 'Status', value: label})
  }

  if (values.fK_VehicleType) {
    const label =
      lookups.vehicleTypes?.find((item: any) => item.key === values.fK_VehicleType)?.value ??
      String(values.fK_VehicleType)
    chips.push({id: 'fK_VehicleType', label: 'Vehicle Type', value: label})
  }

  if (values.fK_BaseLocation) {
    const label =
      lookups.allBaseLocations?.find((item: any) => item.key === values.fK_BaseLocation)?.value ??
      String(values.fK_BaseLocation)
    chips.push({id: 'fK_BaseLocation', label: 'Base Location', value: label})
  }

  const regLabels = (values.regNo as unknown as string[])
    ?.map(
      (key) => lookups.allVehicles?.find((item: any) => item.key === key)?.value ?? String(key)
    )
    .filter(Boolean)
  if (regLabels?.length) {
    chips.push({id: 'regNo', label: 'Vehicle No', value: regLabels.join(', ')})
  }

  if (values.cT_FK_OperatingTypeCode) {
    const label =
      lookups.operatingTypes?.find(
        (item: any) => item.key === values.cT_FK_OperatingTypeCode
      )?.value ?? String(values.cT_FK_OperatingTypeCode)
    chips.push({id: 'cT_FK_OperatingTypeCode', label: 'Operating Type', value: label})
  }

  if (values.cT_FK_CustomerSpecificationCode) {
    const label =
      lookups.customerSpecifications?.find(
        (item: any) => item.key === values.cT_FK_CustomerSpecificationCode
      )?.value ?? String(values.cT_FK_CustomerSpecificationCode)
    chips.push({
      id: 'cT_FK_CustomerSpecificationCode',
      label: 'Customer Spec',
      value: label,
    })
  }

  return chips
}

// Main component for advanced vehicle search functionality
export function AdvanceSearch({
  setFilterOptions,
  onActiveFiltersChange,
  resetToken = 0,
}: JobMonitoringFilterProps) {
  // Get company context from data layer
  const [{company}] = useDataLayerValue()

  // State management for dropdown options
  const [allVehicles, setAllVehicles] = useState<any>([])
  const [vehicleTypes, setVehicleTypes] = useState<any>([])
  const [allBaseLocations, setAllBaseLocations] = useState<any>([])
  const [operatingTypes, setOperatingTypes] = useState<any[]>([])
  const [customerSpecifications, setCustomerSpecifications] = useState<any[]>([])

  // Fetch all necessary data on component mount
  useEffect(() => {
    fetchAllVehicle()
    fetchVehicleTypes()
    fetchAllBaseLocations()
    fetchOperatingTypes()
    fetchCustomerSpecifications()
  }, [])

  const fetchVehicleTypes = async () => {
    try {
      let response = await axios.get(`${API_URL}/transporter/ddlist-vehicle-type/${company}`)
      setVehicleTypes(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchAllVehicle = async () => {
    try {
      let response = await axios.get(`${API_URL}/transporter/ddlist-all-vehicle/${company}`)
      setAllVehicles(response.data.result)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchAllBaseLocations = async () => {
    try {
      let response = await axios.get(
        `${API_URL}/masterdata/ddlist-location-vehicle-base-all/${company}`
      )
      setAllBaseLocations(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchOperatingTypes = async () => {
    try {
      let response = await axios.get(`${API_URL}/masterdata/ddlist-commontable/C90/${company}`)
      setOperatingTypes(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchCustomerSpecifications = async () => {
    try {
      let response = await axios.get(`${API_URL}/masterdata/ddlist-commontable/C45/${company}`)
      setCustomerSpecifications(response.data.data)
    } catch (error) {
      console.log(error)
    }
  }

  // Initialize Formik for form handling
  const formik = useFormik({
    initialValues: filterInitials,
    validationSchema: advanceSerachSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        setFilterOptions(toAppliedFilterOptions(values))
        onActiveFiltersChange?.(
          buildActiveFilterChips(values, {
            vehicleTypes,
            allBaseLocations,
            allVehicles,
            operatingTypes,
            customerSpecifications,
          })
        )
      } catch (error) {
        console.error(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (resetToken === 0) return
    formik.resetForm({values: filterInitials})
    setFilterOptions(toAppliedFilterOptions(filterInitials))
    onActiveFiltersChange?.([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken])

  return (
    <FormikProvider value={formik}>
      <Form>
        <div
          id='kt_accordion_1_body_1'
          className='accordion-collapse collapse p-3 border-0'
          aria-labelledby='kt_accordion_1_header_1'
          data-bs-parent='#kt_accordion_1'
        >
          <div className='row'>
            {/* Status */}
            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className={`form-label`} style={{fontSize: '12px'}}>
                  Status:
                </label>
                <div>
                  <Select
                    {...formik.getFieldProps('statusCode')}
                    options={statusOptions}
                    noOptionsMessage={() => 'No Options'}
                    className={`react-select-styled form-select-sm form-select-solid w-100 m-0 p-0 ${
                      formik.errors?.statusCode && 'is-invalid'
                    }`}
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Status'
                    value={{
                      value: formik.values.statusCode,
                      label: formik.values.statusCode
                        ? statusOptions?.find(
                            (item: any) => item.value === formik.values.statusCode
                          )?.label
                        : 'Select Status',
                    }}
                    isClearable
                    onChange={(selectedOption: any) => {
                      formik.setFieldValue('statusCode', selectedOption?.value ?? '')
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.statusCode && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{String(formik.errors?.statusCode ?? '')}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Vehicle Type:
                </label>
                <div>
                  <Select
                    {...formik.getFieldProps('fK_VehicleType')}
                    options={vehicleTypes?.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className={`react-select-styled form-select-sm form-select-solid w-100 m-0 p-0 ${
                      formik.errors?.fK_VehicleType && 'is-invalid'
                    }`}
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Vehicle Type'
                    value={{
                      value: formik.values.fK_VehicleType,
                      label: formik.values.fK_VehicleType
                        ? vehicleTypes?.find(
                            (item: any) => item.key === formik.values.fK_VehicleType
                          )?.value
                        : 'Select Vehicle Type',
                    }}
                    isClearable
                    onChange={(selectedOptions: any) => {
                      formik.setFieldValue('fK_VehicleType', selectedOptions?.value ?? '')
                    }}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>

            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label ' style={{fontSize: '12px'}}>
                  Base Location:
                </label>
                <div>
                  <Select
                    {...formik.getFieldProps('fK_BaseLocation')}
                    options={allBaseLocations.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className={`react-select-styled form-select-sm form-select-solid w-100 m-0 p-0 ${
                      formik.errors?.fK_BaseLocation && 'is-invalid'
                    }`}
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Base Location'
                    value={{
                      value: formik.values.fK_BaseLocation,
                      label: formik.values.fK_BaseLocation
                        ? allBaseLocations?.find(
                            (item: any) => item.key === formik.values.fK_BaseLocation
                          )?.value
                        : 'Select Base Location',
                    }}
                    isClearable
                    onChange={(selectedOption: any) => {
                      formik.setFieldValue('fK_BaseLocation', selectedOption?.value ?? '')
                    }}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>

            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Vehicle No:
                </label>
                <div>
                  <Select
                    {...formik.getFieldProps('regNo')}
                    options={allVehicles?.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className={`react-select-styled form-select-sm form-select-solid w-100 m-0 p-0 ${
                      formik.errors?.regNo && 'is-invalid'
                    }`}
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Vehicle No'
                    isMulti
                    value={formik.values.regNo?.map((key: string) => ({
                      value: key,
                      label: allVehicles?.find((item: any) => item.key === key)?.value,
                    }))}
                    onChange={(selectedOptions: any) => {
                      const values = selectedOptions
                        ? selectedOptions.map((option: any) => option.value)
                        : []
                      formik.setFieldValue('regNo', values)
                    }}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>

            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Operating Type:
                </label>
                <div>
                  <Select
                    {...formik.getFieldProps('cT_FK_OperatingTypeCode')}
                    options={operatingTypes?.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className='react-select-styled form-select-sm form-select-solid w-100 m-0 p-0'
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Operating Type'
                    isClearable
                    value={{
                      value: formik.values.cT_FK_OperatingTypeCode,
                      label: formik.values.cT_FK_OperatingTypeCode
                        ? operatingTypes?.find(
                            (item: any) => item.key === formik.values.cT_FK_OperatingTypeCode
                          )?.value
                        : 'Select Operating Type',
                    }}
                    onChange={(selectedOption: any) => {
                      formik.setFieldValue(
                        'cT_FK_OperatingTypeCode',
                        selectedOption?.value ?? ''
                      )
                    }}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>

            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12'>
              <div className='mb-3'>
                <label className='form-label' style={{fontSize: '12px'}}>
                  Customer Specification:
                </label>
                <div>
                  <Select
                    {...formik.getFieldProps('cT_FK_CustomerSpecificationCode')}
                    options={customerSpecifications?.map((item: any) => ({
                      value: item.key,
                      label: item.value,
                    }))}
                    noOptionsMessage={() => 'No Options'}
                    className='react-select-styled form-select-sm form-select-solid w-100 m-0 p-0'
                    classNamePrefix='react-select'
                    styles={customStyles}
                    data-kt-select2='true'
                    data-placeholder='Select option'
                    data-allow-clear='true'
                    placeholder='Select Customer Specification'
                    isClearable
                    value={{
                      value: formik.values.cT_FK_CustomerSpecificationCode,
                      label: formik.values.cT_FK_CustomerSpecificationCode
                        ? customerSpecifications?.find(
                            (item: any) =>
                              item.key === formik.values.cT_FK_CustomerSpecificationCode
                          )?.value
                        : 'Select Customer Specification',
                    }}
                    onChange={(selectedOption: any) => {
                      formik.setFieldValue(
                        'cT_FK_CustomerSpecificationCode',
                        selectedOption?.value ?? ''
                      )
                    }}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>

            <div className='col-xl-3 col-lg-4 col-md-6 col-sm-12 d-flex  justify-content-end align-items-end mb-2 w-100'>
              <div className='d-flex'>
                <button
                  type='reset'
                  onClick={(e) => {
                    formik.handleReset(e)
                    formik.handleSubmit()
                    onActiveFiltersChange?.([])
                  }}
                  className='btn btn-sm btn-light btn-active-light-primary me-2'
                >
                  Reset
                </button>

                <button
                  type='button'
                  onClick={() => {
                    formik.handleSubmit()
                  }}
                  disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                  className='btn btn-sm btn-primary'
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </FormikProvider>
  )
}
